"use client";

import { useTranslations } from "next-intl";
import { useMemo } from "react";
import { CopyAnimatedButton } from "@/components/ui/CopyAnimatedButton";
import { Skeleton } from "@/components/ui/skeleton";
import type { components } from "@/types/api/api-types.ts";
import { BlurImage } from "../BlurImage";
import { NFTCardDrawer } from "./NFTCardDrawer";
import { NFTCardEmpty } from "./NFTCardEmpty";

interface NFTCardProps {
  variant?: "purchase" | "history";
  nftData?: components["schemas"]["AvailableNftResponseDto"];
  loading?: boolean;
  error?: boolean;
  nftMarketAddress: string;
  nftCollectionAddress: string;
  isTicketEnabled?: boolean;
  marketTypeId?: string;
}

export const NFTCard = ({
  variant = "purchase",
  nftData,
  nftMarketAddress,
  loading = false,
  error = false,
  nftCollectionAddress,
  isTicketEnabled,
  marketTypeId,
}: NFTCardProps) => {
  const t = useTranslations("nft");
  const price = useMemo(
    () => (nftData?.marketType?.price ? nftData?.marketType?.price : null),
    [nftData],
  );

  if (error) {
    return <NFTCardEmpty />;
  }

  if (loading || !nftData || error) {
    return (
      <div className="flex items-center bg-bg-tertiary p-2 sm:p-3 rounded-sm space-x-4 border border-stroke-subtle max-w-md mx-auto">
        <div className="flex items-center justify-start shrink sm:shrink-0">
          <Skeleton className="w-[130px] h-[130px] rounded-sm" />
        </div>
        <div className="flex flex-col justify-between h-full space-y-2.5 w-full">
          <div className="flex flex-row-reverse items-center sm:items-start justify-between w-full sm:flex-col space-y-3">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-4 w-12" />
          </div>
          <div className="flex items-center justify-between w-full">
            <Skeleton className="h-4 w-12" />
            <Skeleton className="h-4 w-24" />
          </div>
          {variant === "purchase" && <Skeleton className="h-10 w-full" />}
        </div>
      </div>
    );
  }

  return (
    <div className="flex items-center bg-bg-tertiary p-2 sm:p-3 rounded-sm space-x-4 border border-stroke-subtle max-w-md mx-auto">
      <div className="flex items-center justify-start shrink sm:shrink-0">
        <div className="w-[127px] h-[118px] rounded-sm overflow-hidden">
          <BlurImage
            src={nftData.imageUrl ?? "/images/ui/nft-placeholder.svg"}
            alt="NFT Card"
            wrapperClassName="object-cover w-full h-full"
            // Aryosense land images are the full 500x750 card art → zoom-crop to
            // just the land. Other projects (e.g. Crypto Tourist logo) render as-is.
            // arusense.com died; images moved to our own MinIO (aryosense-lands prefix).
            className={`object-cover w-full h-full${nftData.imageUrl?.includes("arusense.com") || nftData.imageUrl?.includes("aryosense-lands") ? " object-[50%_6%] scale-150" : ""}`}
            width={127}
            height={118}
            loading="lazy"
          />
        </div>
      </div>
      <div className="flex flex-col justify-between h-full space-y-3 font-semibold w-full text-xs sm:text-base">
        <div className="flex flex-row-reverse items-center sm:items-start justify-between w-full sm:flex-col space-y-3">
          {variant === "history" ? (
            <div className="text-text-primary flex items-center justify-between gap-2 sm:w-full">
              <p className="">
                {t("card.idLabel", { id: String(nftData.token_id) })}
              </p>
              <CopyAnimatedButton
                text={nftData.token_id.toString()}
                size={14}
              />
            </div>
          ) : (
            <p>{t("card.idLabel", { id: String(nftData.token_id) })}</p>
          )}
          <p>
            {t("card.typeLabel", { plan: String(nftData.marketType.plan) })}
          </p>
        </div>
        <div className="flex items-center justify-between w-full">
          <p>{t("card.valueLabel")}</p>
          <div className="flex items-center gap-1" dir="ltr">
            <p>{price?.sv} USDT</p>
            <span>+</span>
            <p>{price?.bv} UVM</p>
          </div>
        </div>
        {variant === "purchase" && (
          <NFTCardDrawer
            nftData={nftData}
            nftMarketAddress={nftMarketAddress}
            nftCollectionAddress={nftCollectionAddress}
            isTicketEnabled={isTicketEnabled}
            marketTypeId={marketTypeId}
          />
        )}
      </div>
    </div>
  );
};
